import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Container } from "@/components/site/primitives";
import { RelatedLinks } from "@/components/site/RelatedLinks";
import { SITE } from "@/lib/site-content";
import type { LinkPath } from "@/lib/internal-links";
import { ogImageMeta } from "@/lib/og";
import {
  ARTICLE_BY_SLUG,
  ARTICLE_INDEX_UNIQUE,
  isPublished,
  seriesArticles,
} from "@/content/articles/meta";

import { loadArticleBody } from "@/content/articles/load";

/**
 * Archive articles that still earn search impressions, mapped to the current
 * guide that answers the same question. Search Console shows the fractional
 * CTO pricing piece taking the overwhelming majority of the site's
 * impressions at a low average position with no clicks, so the pages that can
 * actually satisfy that intent are linked directly from it rather than being
 * reachable only through the archive list.
 */
const ARTICLE_GUIDES: Partial<Record<string, LinkPath[]>> = {
  "five-reasons-your-startup-needs-a-fractional-cto-in-2025": [
    "/when-to-hire-your-first-cto",
    "/fractional-cto-vs-full-time-vs-agency",
  ],
  "why-a-fractional-cto": ["/what-i-do", "/fractional-cto-vs-full-time-vs-agency"],
  "the-cost-of-not-having-a-cto-and-why-startup-founders-cant-afford-to-get-it-wrong": [
    "/when-to-hire-your-first-cto",
    "/fractional-cto-uk",
  ],
  "what-ai-should-do-in-your-business": ["/ai-governance-for-leadership-teams", "/what-i-do"],
  "who-is-accountable-when-ai-is-confidently-wrong": [
    "/ai-governance-for-leadership-teams",
    "/board-advisory",
  ],
  "13-ai-governance-decisions-for-leadership-teams": [
    "/ai-governance-for-leadership-teams",
    "/board-advisory",
    "/what-i-do",
  ],
  "how-good-governance-is-crucial-to-the-success-of-any-tech-project": [
    "/what-a-technology-non-executive-director-does",
    "/technology-non-executive-director-uk",
  ],
};

function formatDate(iso: string) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

export const Route = createFileRoute("/articles/$slug")({
  loader: async ({ params }) => {
    const meta = ARTICLE_BY_SLUG.get(params.slug);
    if (!meta) throw notFound();
    const body = await loadArticleBody(params.slug);
    if (!body) throw notFound();
    return { meta, body };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Article not found | Scryla" }, { name: "robots", content: "noindex" }],
      };
    }
    const { meta } = loaderData;
    const url = `${SITE.url}/articles/${params.slug}`;
    // Legacy duplicate posts stay reachable but point search engines at the original.
    const canonical = meta.duplicateOf ? `${SITE.url}/articles/${meta.duplicateOf}` : url;
    const rawImage = meta.image || undefined;
    // Social crawlers require absolute URLs; article images are stored site-relative.
    const image = rawImage
      ? rawImage.startsWith("http")
        ? rawImage
        : `${SITE.url}${rawImage}`
      : undefined;

    // Long editorial headlines are truncated in search results, so a shorter
    // seoTitle is used for the <title> tag only. The on-page H1 is unchanged.
    const headTitle = meta.seoTitle ?? meta.title;
    const pageTitle =
      headTitle.length + " | Scryla".length <= 60 ? `${headTitle} | Scryla` : headTitle;

    return {
      meta: [
        { title: pageTitle },
        // Scheduled pieces stay reachable by direct link for review, but are
        // kept out of search until their publication date passes.
        ...(isPublished(meta) ? [] : [{ name: "robots", content: "noindex" }]),

        { name: "description", content: meta.description },
        { property: "og:title", content: meta.title },
        { property: "og:description", content: meta.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: "Scryla" },
        { property: "article:published_time", content: meta.date },
        { property: "article:author", content: SITE.person },
        { name: "twitter:title", content: meta.title },
        { name: "twitter:description", content: meta.description },
        ...(image
          ? [
              { property: "og:image", content: image },
              { property: "og:image:alt", content: meta.title },
              { name: "twitter:card", content: "summary_large_image" },
              { name: "twitter:image", content: image },
              { name: "twitter:image:alt", content: meta.title },
            ]
          : ogImageMeta("article", "Article, notes on technology leadership and governance")),
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: meta.title,
            description: meta.answer ?? meta.description,
            datePublished: meta.date,
            dateModified: meta.updated ?? meta.date,
            wordCount: meta.words,
            inLanguage: "en-GB",
            mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
            author: {
              "@type": "Person",
              name: SITE.person,
              // Bylines resolve to the one canonical bio page, not an off-site
              // profile, so every article strengthens the same entity.
              url: `${SITE.url}/who-i-am`,
              sameAs: [SITE.linkedin],
            },
            publisher: {
              "@type": "Organization",
              name: "Scryla Consultancy Ltd",
              logo: { "@type": "ImageObject", url: `${SITE.url}${SITE.logo}` },
            },
            ...(image ? { image: [image] } : {}),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
              { "@type": "ListItem", position: 2, name: "Articles", item: `${SITE.url}/articles` },
              { "@type": "ListItem", position: 3, name: meta.title, item: url },
            ],
          }),
        },
        // Question and answer pairs are the format search and AI answer engines
        // quote from. Only emitted where the visible page carries the same text.
        ...(meta.faq && meta.faq.length > 0 && !meta.duplicateOf
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: meta.faq.map((item) => ({
                    "@type": "Question",
                    name: item.q,
                    acceptedAnswer: { "@type": "Answer", text: item.a },
                  })),
                }),
              },
            ]
          : []),
      ],
    };
  },
  notFoundComponent: ArticleNotFound,
  component: ArticlePage,
});

function ArticleNotFound() {
  return (
    <Container>
      <h1 className="text-3xl font-bold tracking-[-0.03em]">That article is not here</h1>
      <p className="mt-3 text-body">
        It may have been retired when the site was rebuilt. Everything still published is on the
        articles page.
      </p>
      <Link
        to="/articles"
        className="mt-6 inline-flex rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
      >
        Browse all articles
      </Link>
    </Container>
  );
}

function ArticlePage() {
  const { meta, body } = Route.useLoaderData();
  const related = ARTICLE_INDEX_UNIQUE.filter(
    (a) => a.slug !== meta.slug && a.slug !== meta.duplicateOf,
  ).slice(0, 3);
  const minutes = Math.max(1, Math.round(meta.words / 220));
  const guidePaths = ARTICLE_GUIDES[meta.slug] ?? [];
  // Series navigation only lists parts that are actually live, so a staged
  // piece never links readers to a page that is not published yet.
  const seriesParts = meta.series
    ? seriesArticles(meta.series).filter((a) => isPublished(a) || a.slug === meta.slug)
    : [];

  return (
    <article>
      <header className="border-b border-border bg-gradient-to-b from-background to-surface">
        <Container>
          <nav aria-label="Breadcrumb" className="text-sm">
            <ol className="flex flex-wrap items-center gap-2 text-muted-foreground">
              <li>
                <Link to="/" className="hover:text-primary">
                  Home
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li>
                <Link to="/articles" className="hover:text-primary">
                  Articles
                </Link>
              </li>
            </ol>
          </nav>
          <h1 className="mt-5 max-w-[22ch] text-[34px] leading-[1.06] font-bold tracking-[-0.035em] md:max-w-[24ch] md:text-5xl">
            {meta.title}
          </h1>
          <p className="mt-5 text-sm text-muted-foreground">
            By{" "}
            <Link to="/who-i-am" className="font-medium text-foreground hover:text-primary">
              {SITE.person}
            </Link>
            <span aria-hidden="true"> · </span>
            <time dateTime={meta.date}>{formatDate(meta.date)}</time>
            {meta.updated && meta.updated !== meta.date ? (
              <>
                <span aria-hidden="true"> · </span>
                Reviewed <time dateTime={meta.updated}>{formatDate(meta.updated)}</time>
              </>
            ) : null}
            <span aria-hidden="true"> · </span>
            {minutes} min read
          </p>
          {/* The answer reads as a standfirst rather than a boxed callout: a
              stack of bordered panels above the body made the page feel
              assembled from fragments instead of written. */}
          {meta.answer ? (
            <p className="mt-6 max-w-[62ch] text-[19px] leading-[1.6] text-foreground md:text-xl">
              {meta.answer}
            </p>
          ) : null}
        </Container>
      </header>

      <div className="border-b border-border">
        <Container>
          {meta.takeaways && meta.takeaways.length > 0 ? (
            <section
              className="mb-12 max-w-[68ch] rounded-lg border border-border bg-surface px-6 py-5"
              aria-labelledby="takeaways-heading"
            >
              <h2
                id="takeaways-heading"
                className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase"
              >
                Key points
              </h2>
              <ul className="mt-3 list-disc space-y-2 pl-5 marker:text-primary">
                {meta.takeaways.map((point) => (
                  <li key={point} className="text-body">
                    {point}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <div
            className="article-prose max-w-[68ch]"
            // Article bodies are migrated from the original site and sanitised at build time to a
            // fixed allow-list of semantic tags (no scripts, styles, or event attributes).
            dangerouslySetInnerHTML={{ __html: body }}
          />
          {meta.faq && meta.faq.length > 0 ? (
            <section className="mt-12 max-w-[68ch]" aria-labelledby="faq-heading">
              <h2 id="faq-heading" className="text-2xl font-bold tracking-[-0.03em] md:text-[28px]">
                Common questions
              </h2>
              <dl className="mt-6 space-y-6">
                {meta.faq.map((item) => (
                  <div key={item.q}>
                    <dt className="text-base font-semibold text-foreground">{item.q}</dt>
                    <dd className="mt-2 text-body">{item.a}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ) : null}
          {seriesParts.length > 1 ? (
            <nav className="mt-12 max-w-[68ch]" aria-labelledby="series-heading">
              <h2 id="series-heading" className="text-lg font-semibold tracking-[-0.02em]">
                Part of the AI governance series
              </h2>
              <ol className="mt-4 space-y-3">
                {seriesParts.map((part, index) => (
                  <li key={part.slug} className="text-body">
                    <span className="text-muted-foreground">{index + 1}.</span>{" "}
                    {part.slug === meta.slug ? (
                      <span aria-current="page" className="font-medium text-foreground">
                        {part.title}
                      </span>
                    ) : (
                      <Link
                        to="/articles/$slug"
                        params={{ slug: part.slug }}
                        className="font-medium text-foreground underline underline-offset-4 hover:text-primary"
                      >
                        {part.title}
                      </Link>
                    )}
                  </li>
                ))}
              </ol>
              <p className="mt-4">
                <Link
                  to="/ai-governance-for-leadership-teams"
                  className="font-medium text-primary underline underline-offset-4"
                >
                  Read the AI governance series overview
                </Link>
              </p>
            </nav>
          ) : null}
          {guidePaths.length > 0 ? (
            <div className="mt-10 max-w-[68ch]">
              <h2 className="text-lg font-semibold tracking-[-0.02em]">
                Where this is covered in full
              </h2>
              <RelatedLinks
                className="mt-4"
                variant="cards"
                label="Current guides on this topic"
                paths={guidePaths}
              />
            </div>
          ) : null}
        </Container>
      </div>

      <section className="border-b border-border bg-surface" aria-labelledby="related-heading">
        <Container>
          <h2 id="related-heading" className="text-2xl font-bold tracking-[-0.03em] md:text-[32px]">
            More articles
          </h2>
          <ul className="mt-6 grid gap-5 md:grid-cols-3">
            {related.map((a) => (
              <li key={a.slug} className="rounded-lg border border-border bg-card p-6">
                <p className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                  <time dateTime={a.date}>{formatDate(a.date)}</time>
                </p>
                <h3 className="mt-3 text-lg font-semibold">
                  <Link
                    to="/articles/$slug"
                    params={{ slug: a.slug }}
                    className="hover:text-primary"
                  >
                    {a.title}
                  </Link>
                </h3>
              </li>
            ))}
          </ul>
          <p className="mt-8">
            <Link
              to="/contact"
              className="inline-flex rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
            >
              Talk to me about your business
            </Link>
          </p>
        </Container>
      </section>
    </article>
  );
}
